import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Flex,
    Text,
    useMultiStyleConfig,
    Stack,
    SimpleGrid,
    Button,
    useDisclosure,
} from '@chakra-ui/react'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import {MRT_API_BASEPATH} from '../../ssr/constants'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useParams, Link} from 'react-router-dom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {FormattedMessage, useIntl} from 'react-intl'
import {remCalc} from '../../theme/_utils'
import SubscriptionsBox from '../../components/subscriptions-box'
import {brandIcons} from './payment-methods/partials/StoredCard'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import SubscriptionEditAddressModal from '../../components/subscription-edit-modals/subscription-edit-address-modal'
import SubscriptionEditNotAllowedModal from '../../components/subscription-edit-modals/subscription-edit-not-allowed-modal'
import SubscriptionEditPaymentModal from '../../components/subscription-edit-modals/subscription-edit-payment-modal'
import SubscriptionEditBillingAddressModal from '../../components/subscription-edit-modals/subscription-edit-billing-address-modal'
import {isEditOrCancelHidden, canEditOrCancelSubscription} from './my-orders/utils'
import {useTokenHelper} from '../../utils/token-helper'

const SubscriptionDetails = () => {
    const {subscriptionId} = useParams()
    const [subscriptionDetails, setSubscriptionDetails] = useState(null)
    const [isLoading, setIsLoading] = useState(false) // Don't start loading immediately
    const appOrigin = useOrigin(false)
    const {locale} = useMultiSite()
    const headingRef = useRef()
    const localeId = locale.id.toLowerCase()
    const styles = useMultiStyleConfig('Subscriptions')
    const {data: customer} = useCurrentCustomer()
    const {getTokenWhenReady} = useAccessToken()
    const {isOpen, onOpen, onClose} = useDisclosure()
    const paymentModal = useDisclosure()
    const billingModal = useDisclosure()
    const [notAllowedModal, setNotAllowedModal] = useState({isOpen: false, title: '', message: ''})
    const {formatMessage} = useIntl()
    const {fetchOMSValidToken} = useTokenHelper()

    const handleGetSubscriptionDetails = async () => {
        if (!subscriptionId) return // Prevents API call if there's no subscriptionId

        try {
            setIsLoading(true)
            const accessToken = await fetchOMSValidToken()
            const token = await getTokenWhenReady()
            // Use the token from getTokenWhenReady for Commerce Cloud APIs
            // and accessToken for OMS APIs

            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/get-subscription-details-oms?subscriptionId=${subscriptionId}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`, // OMS token
                        customerId: customer?.customerId,
                        'x-commercecloud-token': `Bearer ${token}`, // Send Commerce Cloud token as a custom header
                    },
                },
            )

            const result = await response.json()
            if (result.success) {
                setSubscriptionDetails(result.subscription)
            }
        } catch (error) {
            console.error('Error fetching subscription details:', error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (subscriptionId && customer?.customerId) {
            handleGetSubscriptionDetails()
        }
    }, [subscriptionId, customer?.customerId])

    // Prepare current address object for the modal
    const currentAddress = subscriptionDetails
        ? {
              name: `${subscriptionDetails?.account?.firstName || ''} ${
                  subscriptionDetails?.account?.lastName || ''
              }`,
              //phone: '348 7201094', // Replace with real data if available
              street: subscriptionDetails?.shippingAddress?.street,
              city: subscriptionDetails?.shippingAddress?.city,
              postalCode: subscriptionDetails?.shippingAddress?.postalCode,
              countryCode: subscriptionDetails?.shippingAddress?.countryCode,
          }
        : {}

    // Prepare current billing address object for the modal
    const currentBillingAddress = subscriptionDetails
        ? {
              name: `${subscriptionDetails?.account?.firstName || ''} ${
                  subscriptionDetails?.account?.lastName || ''
              }`,
              street: subscriptionDetails?.billingAddress?.street,
              city: subscriptionDetails?.billingAddress?.city,
              postalCode: subscriptionDetails?.billingAddress?.postalCode,
              countryCode: subscriptionDetails?.billingAddress?.countryCode,
          }
        : {}

    // Handler for edit buttons
    const handleEditClick = (type) => {
        if (canEditOrCancelSubscription(subscriptionDetails)) {
            if (type === 'delivery') onOpen()
            else if (type === 'payment') paymentModal.onOpen()
            else if (type === 'billing') billingModal.onOpen()
        } else {
            let title = ''
            let message = ''
            // Calculate the last editable date (2 days before renewal)
            let editableUntilStr = ''
            if (subscriptionDetails?.renewal?.nextDate) {
                const nextDate = new Date(subscriptionDetails.renewal.nextDate)
                const editableUntil = new Date(nextDate)
                editableUntil.setDate(editableUntil.getDate() - 2)
                editableUntilStr = editableUntil.toLocaleDateString()
            }
            if (type === 'payment') {
                title = formatMessage({
                    id: 'subscription_edit_not_allowed.payment_title',
                    defaultMessage: 'Payment Method',
                })
                message = formatMessage(
                    {
                        id: 'subscription_edit_not_allowed.payment_message',
                        defaultMessage:
                            'The payment method could be changed until {date}. You will be able to change the payment method for the next order.',
                    },
                    {date: editableUntilStr},
                )
            } else if (type === 'delivery') {
                title = formatMessage({
                    id: 'subscription_edit_not_allowed.delivery_title',
                    defaultMessage: 'Shipping address',
                })
                message = formatMessage(
                    {
                        id: 'subscription_edit_not_allowed.delivery_message',
                        defaultMessage:
                            'The shipping address could be changed until {date}. You will be able to change the shipping address for the next order.',
                    },
                    {date: editableUntilStr},
                )
            } else if (type === 'billing') {
                title = formatMessage({
                    id: 'subscription_edit_not_allowed.billing_title',
                    defaultMessage: 'Billing address',
                })
                message = formatMessage(
                    {
                        id: 'subscription_edit_not_allowed.billing_message',
                        defaultMessage:
                            'The billing address could be changed until {date}. You will be able to change the billing address for the next order.',
                    },
                    {date: editableUntilStr},
                )
            }
            setNotAllowedModal({isOpen: true, title, message})
        }
    }

    // Expose refresh function for children
    const refreshSubscriptionDetails = async () => {
        await handleGetSubscriptionDetails()
    }

    return (
        <Stack gap="0" data-testid="test-get-subscription-details-page">
            <Seo title="Subscription Details" description="View details of your subscription" />
            <Text
                {...styles.subscriptionDetailsLabel}
                marginTop={remCalc(24)}
                as={Link}
                to={`/${localeId}/account/subscription-history`}
            >
                <FormattedMessage
                    id="account_subscription_detail.back_button"
                    defaultMessage="Back to subscription history"
                />
            </Text>

            {/* Loading State */}
            {isLoading ? (
                <Text>Loading subscription details...</Text>
            ) : (
                subscriptionDetails && (
                    <>
                        <Text {...styles.accountTitleDetails} tabIndex="0" ref={headingRef}>
                            <FormattedMessage
                                defaultMessage="Subscription: {subscriptionNumber}"
                                id="account_subscription_details.title"
                                values={{
                                    subscriptionNumber: subscriptionDetails.name,
                                }}
                            />
                        </Text>

                        <SubscriptionsBox subscription={subscriptionDetails} detailPage={true} />
                        <Box {...styles.subscriptionBoxContainer} {...styles.subscriptionGap}>
                            <SimpleGrid
                                columns={{base: 1, md: 4}}
                                gap={remCalc(24)}
                                alignItems="stretch"
                            >
                                <Box height="100%" display="flex" flexDirection="column">
                                    <Text {...styles.bodyMedium} fontWeight="bold">
                                        <FormattedMessage
                                            id="account_order_detail.label.delivery_method"
                                            defaultMessage="Delivery Method"
                                        />
                                    </Text>
                                    <Text {...styles.bodyMedium}>
                                        <FormattedMessage
                                            id="account_subscription_details.label.shipment_method"
                                            defaultMessage="Subscription shipping - 3 to 5 working days"
                                        />
                                    </Text>
                                </Box>

                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    flex="1"
                                    alignItems="flex-start"
                                    height="100%"
                                    data-cs-mask
                                >
                                    <Text {...styles.bodyMedium} fontWeight="bold">
                                        <FormattedMessage
                                            id="account_order_detail.label.delivery_address"
                                            defaultMessage="Delivery Address"
                                        />
                                    </Text>
                                    <ul style={{...styles.subscriptionDetailsList}}>
                                        <li>
                                            <b>
                                                {subscriptionDetails?.account?.firstName}{' '}
                                                {subscriptionDetails?.account?.lastName}
                                            </b>
                                        </li>
                                        <li>{subscriptionDetails?.shippingAddress?.street}</li>
                                        <li>{subscriptionDetails?.shippingAddress?.city}</li>
                                        <li>{subscriptionDetails?.shippingAddress?.postalCode}</li>
                                        <li>{subscriptionDetails?.shippingAddress?.countryCode}</li>
                                    </ul>
                                    {!isEditOrCancelHidden(subscriptionDetails?.status) && (
                                        <Button
                                            variant="link"
                                            color="text.tertiary"
                                            fontWeight="normal"
                                            style={{
                                                textDecoration: 'underline',
                                                paddingTop: remCalc(24),
                                            }}
                                            onClick={() => handleEditClick('delivery')}
                                            mt="auto"
                                            alignSelf="flex-start"
                                            data-testid="delivery-edit-button"
                                        >
                                            <FormattedMessage
                                                id="checkout_payment.label.edit"
                                                defaultMessage="Edit"
                                            />
                                        </Button>
                                    )}
                                </Box>

                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    flex="1"
                                    alignItems="flex-start"
                                    height="100%"
                                    data-cs-mask
                                >
                                    <Text {...styles.bodyMedium} fontWeight="bold">
                                        <FormattedMessage
                                            id="account_order_detail.label.billing_address"
                                            defaultMessage="Billing Address"
                                        />
                                    </Text>
                                    <ul style={{...styles.subscriptionDetailsList}}>
                                        <li>
                                            <b>
                                                {subscriptionDetails?.account?.firstName}{' '}
                                                {subscriptionDetails?.account?.lastName}
                                            </b>
                                        </li>
                                        <li>{subscriptionDetails?.billingAddress?.street}</li>
                                        <li>{subscriptionDetails?.billingAddress?.city}</li>
                                        <li>{subscriptionDetails?.billingAddress?.postalCode}</li>
                                        <li>{subscriptionDetails?.billingAddress?.countryCode}</li>
                                    </ul>
                                    {!isEditOrCancelHidden(subscriptionDetails?.status) && (
                                        <Button
                                            variant="link"
                                            color="text.tertiary"
                                            fontWeight="normal"
                                            style={{
                                                textDecoration: 'underline',
                                                paddingTop: remCalc(24),
                                            }}
                                            onClick={() => handleEditClick('billing')}
                                            mt="auto"
                                            alignSelf="flex-start"
                                            data-testid="billing-edit-button"
                                        >
                                            <FormattedMessage
                                                id="checkout_payment.label.edit"
                                                defaultMessage="Edit"
                                            />
                                        </Button>
                                    )}
                                </Box>

                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    flex="1"
                                    alignItems="flex-start"
                                    height="100%"
                                    data-cs-mask
                                >
                                    <Text fontWeight="bold">
                                        <FormattedMessage
                                            id="account_order_detail.label.payment_method"
                                            defaultMessage="Payment Method"
                                        />
                                    </Text>
                                    <Box display="flex">
                                        {
                                            brandIcons[
                                                subscriptionDetails?.paymentDetails?.method?.toLowerCase()
                                            ]
                                        }
                                        <Text fontWeight="bold">
                                            <FormattedMessage
                                                id="account_subscription_details.detail.payment_method"
                                                defaultMessage="ends with {value}"
                                                values={{
                                                    value: subscriptionDetails?.paymentDetails
                                                        ?.additionalInfo,
                                                }}
                                            />
                                        </Text>
                                    </Box>
                                    <Box {...styles.infoContainer}>
                                        <Flex {...styles.infoNameContainer}>
                                            <Text {...styles.infoLabel}>
                                                <FormattedMessage
                                                    id="payment_methods.card.name"
                                                    defaultMessage="Name of the card"
                                                />
                                            </Text>
                                            <Text {...styles.infoValue}>
                                                {subscriptionDetails?.paymentDetails?.owner}
                                            </Text>
                                        </Flex>
                                        <Flex {...styles.infoExpirationContainer}>
                                            <Text {...styles.infoLabel}>
                                                <FormattedMessage
                                                    id="payment_methods.card.expDate"
                                                    defaultMessage="Expiration date"
                                                />
                                            </Text>
                                            <Text {...styles.infoValue}>
                                                {subscriptionDetails?.paymentDetails?.expiryDate}
                                            </Text>
                                        </Flex>
                                    </Box>
                                    {!isEditOrCancelHidden(subscriptionDetails?.status) && (
                                        <Button
                                            variant="link"
                                            color="text.tertiary"
                                            fontWeight="normal"
                                            style={{
                                                textDecoration: 'underline',
                                                paddingTop: remCalc(24),
                                            }}
                                            onClick={() => handleEditClick('payment')}
                                            mt="auto"
                                            alignSelf="flex-start"
                                            data-testid="payment-edit-button"
                                        >
                                            <FormattedMessage
                                                id="checkout_payment.label.edit"
                                                defaultMessage="Edit"
                                            />
                                        </Button>
                                    )}
                                </Box>
                            </SimpleGrid>
                        </Box>

                        <SubscriptionEditAddressModal
                            isOpen={isOpen}
                            onClose={onClose}
                            currentAddress={currentAddress}
                            subscriptionId={subscriptionId}
                            onSuccess={refreshSubscriptionDetails}
                        />
                        <SubscriptionEditBillingAddressModal
                            isOpen={billingModal.isOpen}
                            onClose={billingModal.onClose}
                            currentAddress={currentBillingAddress}
                            subscriptionId={subscriptionId}
                            onSuccess={refreshSubscriptionDetails}
                        />
                        <SubscriptionEditPaymentModal
                            isOpen={paymentModal.isOpen}
                            onClose={paymentModal.onClose}
                            currentPayment={subscriptionDetails?.paymentDetails}
                            subscriptionId={subscriptionId}
                            onSuccess={refreshSubscriptionDetails}
                        />
                        <SubscriptionEditNotAllowedModal
                            isOpen={notAllowedModal.isOpen}
                            onClose={() => setNotAllowedModal({...notAllowedModal, isOpen: false})}
                            title={notAllowedModal.title}
                            message={notAllowedModal.message}
                        />
                    </>
                )
            )}
        </Stack>
    )
}

SubscriptionDetails.getTemplateName = () => 'get-subscription-details'
SubscriptionDetails.propTypes = {subscriptionId: PropTypes.string}

export default SubscriptionDetails
